// HeadlessReportController.js
//
// Self-contained controller for the headless report routes
// (/generate-dxf, /generate-facet, /generate-shade).
//
// It is intentionally isolated: it does NOT touch the interactive Designer,
// AppHeader, or any existing report flow. It only owns the three "result
// channels" that another application can consume:
//
//   1. Backend upload  -> performed by the report generators themselves
//                         (uploadDesignerReport). This is the source of truth.
//   2. Redirect        -> if a `returnUrl` is supplied, the browser is sent
//                         back to it with ?status=...&type=...&reason=...
//   3. On-page status  -> a machine-readable JSON snapshot is exposed both on
//                         `window.__REPORT_STATUS__` and inside a DOM element
//                         (#report-status) so an opener/iframe can scrape it.
//
// Lifecycle phases (status.phase):
//   "pending" -> "authenticating" -> "loading-design" -> "loading-model"
//   -> "computing-irradiance" -> "generating" -> "done" | "error"

export const REPORT_PHASES = Object.freeze({
  PENDING: "pending",
  AUTHENTICATING: "authenticating",
  LOADING_DESIGN: "loading-design",
  LOADING_MODEL: "loading-model",
  COMPUTING_IRRADIANCE: "computing-irradiance",
  GENERATING: "generating",
  DONE: "done",
  ERROR: "error",
});

const STATUS_DOM_ID = "report-status";
const STATUS_WINDOW_KEY = "__REPORT_STATUS__";

export class HeadlessReportController {
  /**
   * @param {Object} opts
   * @param {string} opts.reportType  - "dxf" | "facet" | "shade"
   * @param {string} [opts.returnUrl] - optional URL to redirect to when finished
   * @param {(status: object) => void} [opts.onChange] - React state setter
   */
  constructor({ reportType, returnUrl = "", onChange } = {}) {
    this.reportType = reportType || "";
    this.returnUrl = returnUrl || "";
    this.onChange = typeof onChange === "function" ? onChange : () => {};
    this.finished = false;
    this.status = {
      reportType: this.reportType,
      phase: REPORT_PHASES.PENDING,
      ok: null,
      progress: 0,
      message: "",
      reason: "",
      startedAt: null, // stamped lazily (Date.now is unavailable in some envs)
      updatedAt: null,
    };
  }

  /** Push the current status to React, window, and the DOM mirror. */
  _emit() {
    const snapshot = { ...this.status };

    // React
    this.onChange(snapshot);

    if (typeof window !== "undefined") {
      // window global (opener can read window.__REPORT_STATUS__)
      window[STATUS_WINDOW_KEY] = snapshot;

      // DOM mirror (opener/iframe can scrape #report-status textContent)
      if (typeof document !== "undefined") {
        let node = document.getElementById(STATUS_DOM_ID);
        if (!node) {
          node = document.createElement("script");
          node.type = "application/json";
          node.id = STATUS_DOM_ID;
          document.body && document.body.appendChild(node);
        }
        try {
          node.textContent = JSON.stringify(snapshot);
        } catch (e) {
          /* ignore serialization issues */
        }
      }
    }
  }

  /** Advance the lifecycle phase. */
  setPhase(phase, { progress, message } = {}) {
    if (this.finished) return;
    this.status = {
      ...this.status,
      phase,
      ...(typeof progress === "number" ? { progress } : {}),
      ...(typeof message === "string" ? { message } : {}),
    };
    this._emit();
  }

  /** Mark success and (optionally) redirect. */
  succeed(message = "Report generated.") {
    if (this.finished) return;
    this.finished = true;
    this.status = {
      ...this.status,
      phase: REPORT_PHASES.DONE,
      ok: true,
      progress: 100,
      message,
      reason: "",
    };
    this._emit();
    this._redirect("ok", "");
  }

  /**
   * Mark failure and (optionally) redirect.
   * @param {string} reason - short machine-friendly code, e.g. "unauthenticated"
   * @param {string} [message] - human readable detail
   */
  fail(reason, message = "") {
    if (this.finished) return;
    this.finished = true;
    this.status = {
      ...this.status,
      phase: REPORT_PHASES.ERROR,
      ok: false,
      message: message || reason || "Report generation failed.",
      reason: reason || "error",
    };
    this._emit();
    this._redirect("error", reason || "error");
  }

  _redirect(status, reason) {
    if (!this.returnUrl || typeof window === "undefined") return;
    try {
      const url = new URL(this.returnUrl, window.location.origin);
      url.searchParams.set("status", status);
      url.searchParams.set("type", this.reportType);
      if (reason) url.searchParams.set("reason", reason);
      // replace() so the headless page does not pollute history.
      window.location.replace(url.toString());
    } catch (e) {
      // Bad returnUrl: keep the on-page status instead of crashing.
      console.error("HeadlessReport redirect failed:", e);
    }
  }
}

export default HeadlessReportController;
